// Shared constants and calendar helpers for the customer dashboard

export const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const TIMES = [
  "08:00",
  "09:00",
  "10:00",
  "11:00",
  "12:00",
  "13:00",
  "14:00",
  "15:00",
  "16:00",
  "17:00",
];

export const FREQUENCIES = [
  { value: "weekly", label: "Weekly (15% off)", discount: "15%" },
  { value: "fortnightly", label: "Fortnightly (10% off)", discount: "10%" },
  { value: "monthly", label: "Monthly (5% off)", discount: "5%" },
];

export const EQUIPMENT_OPTIONS = [
  {
    value: "full",
    label: "Full Equipment (We bring everything)",
    description: "Our team brings all cleaning supplies and equipment",
  },
  {
    value: "basic",
    label: "Basic Equipment (We bring basics)",
    description: "We bring standard supplies, you provide vacuum/mop",
  },
  {
    value: "customer_provides",
    label: "Customer Provides",
    description: "You provide all cleaning supplies and equipment",
  },
];

export const generateCleaningDates = (recurringBooking, monthsAhead = 3) => {
  const dates = [];
  if (recurringBooking.status !== "active") return dates;

  const dayMap = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
  };
  const targetDay = dayMap[recurringBooking.preferred_day];

  const startDate = recurringBooking.next_booking_date
    ? new Date(recurringBooking.next_booking_date)
    : new Date();

  const endDate = new Date();
  endDate.setMonth(endDate.getMonth() + monthsAhead);

  const currentDate = new Date(startDate);
  while (currentDate.getDay() !== targetDay) {
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const frequencyDays = { weekly: 7, fortnightly: 14, monthly: 28 };
  const interval = frequencyDays[recurringBooking.frequency] || 7;

  while (currentDate <= endDate) {
    dates.push({ date: new Date(currentDate), booking: recurringBooking });
    currentDate.setDate(currentDate.getDate() + interval);
  }
  return dates;
};

const formatDateForICS = (date, time) => {
  const [hours, minutes] = time.split(":");
  const d = new Date(date);
  d.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  return d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
};

export const generateICSFile = (booking, date) => {
  const startTime = booking.preferred_time || "09:00";
  const [hours, minutes] = startTime.split(":");
  const endHours = parseInt(hours) + 2;
  const endTime = `${endHours.toString().padStart(2, "0")}:${minutes}`;

  const startDT = formatDateForICS(date, startTime);
  const endDT = formatDateForICS(date, endTime);

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Clean Kin//Cleaning Service//EN
BEGIN:VEVENT
UID:${booking.id}-${date.toISOString()}@cleankinservices.co.uk
DTSTAMP:${formatDateForICS(new Date(), "00:00")}
DTSTART:${startDT}
DTEND:${endDT}
SUMMARY:Clean Kin - ${booking.service_name}
DESCRIPTION:${booking.service_name} cleaning service at ${booking.address}. ${
    booking.special_instructions ? "Notes: " + booking.special_instructions : ""
  }
LOCATION:${booking.address}, ${booking.postcode}
END:VEVENT
END:VCALENDAR`;
};

export const downloadICSFile = (booking, date) => {
  const icsContent = generateICSFile(booking, date);
  const blob = new Blob([icsContent], {
    type: "text/calendar;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `cleankin-${booking.service_name
    .replace(/\s+/g, "-")
    .toLowerCase()}-${date.toISOString().split("T")[0]}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateGoogleCalendarUrl = (booking, date) => {
  const startTime = booking.preferred_time || "09:00";
  const [hours, minutes] = startTime.split(":");
  const endHours = parseInt(hours) + 2;

  const startDate = new Date(date);
  startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  const endDate = new Date(date);
  endDate.setHours(endHours, parseInt(minutes), 0, 0);

  const formatForGoogle = (d) =>
    d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Clean Kin - ${booking.service_name}`,
    dates: `${formatForGoogle(startDate)}/${formatForGoogle(endDate)}`,
    details: `${booking.service_name} cleaning service.\n\nAddress: ${
      booking.address
    }, ${booking.postcode}\n${
      booking.special_instructions
        ? "Notes: " + booking.special_instructions
        : ""
    }`,
    location: `${booking.address}, ${booking.postcode}`,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const generateOutlookCalendarUrl = (booking, date) => {
  const startTime = booking.preferred_time || "09:00";
  const [hours, minutes] = startTime.split(":");
  const endHours = parseInt(hours) + 2;

  const startDate = new Date(date);
  startDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
  const endDate = new Date(date);
  endDate.setHours(endHours, parseInt(minutes), 0, 0);

  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: `Clean Kin - ${booking.service_name}`,
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
    body: `${booking.service_name} cleaning service at ${booking.address}, ${booking.postcode}`,
    location: `${booking.address}, ${booking.postcode}`,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

export const getStatusBadgeStyle = (status) => {
  const styles = {
    pending: "bg-amber-100 text-amber-700",
    confirmed: "bg-green-100 text-green-700",
    completed: "bg-blue-100 text-blue-700",
    cancelled: "bg-red-100 text-red-700",
    active: "bg-green-100 text-green-700",
    paused: "bg-amber-100 text-amber-700",
    paid: "bg-green-100 text-green-700",
  };
  return styles[status] || "bg-gray-100 text-gray-600";
};
